// eslint-disable-next-line no-unused-vars
import { Body1, tokens } from '@fluentui/react-components';
import { customFonts } from '../../styles';
import { ChatMessage } from '../../domain/messages/models/ChatMessage';

/*
 * Function to detect and convert URLs within a string into clickable links.
 * It wraps each link matched with anchor tags and applies safe href attributes.
 */
export function convertToAnchorTags(htmlString: string) {
    // Regular expression to match links, excluding any HTML tags at the end
    // Since response from bot is plain text, sometimes line breaks and other html tags are included in the response for readability.
    const linkRegex =
        /(?:https?):\/\/(\w+:?\w*)?(\S+)(:\d+)?(\/|\/([\w#!:.?+=&%!\-/]))?(?=(<br|<p|<div|<span)\s*\/>|$)/g;

    const result = htmlString?.replace(linkRegex, function (link) {
        try {
            // Basic validation to check if the link is a valid URL
            if (!/^https?:\/\/\S+$/.test(link)) {
                throw new Error('Invalid URL format');
            }

            // Parse URL first -- URL class handles cybersecurity concerns related to URL parsing and manipulation
            const safeHref = new URL(link).toString();

            // Replace each link with anchor tags
            return `<a href="${safeHref}">${link}</a>`;
        } catch (e) {
            console.error(`Invalid URL: ${link}`, e);
            // Return the original link if it's invalid
            return link;
        }
    });

    return result;
}

/*
 * Function to check if date is today.
 */
export function isToday(date: Date) {
    return date.toDateString() !== new Date().toDateString();
}

/*
 * Function to render the date and/or time of a message.
 */
export function timestampToDateString(timestamp: number, alwaysShowTime = false) {
    const date = new Date(timestamp);
    const dateString = date.toLocaleDateString([], {
        month: 'numeric',
        day: 'numeric',
    });
    const timeString = date.toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
    });

    return date.toDateString() !== new Date().toDateString()
        ? alwaysShowTime
            ? dateString + ' ' + timeString // if the date is not today and we are always showing the time, show the date and time
            : dateString // if the date is not today and we are not always showing the time, only show the date
        : timeString; // if the date is today, only show the time
}

/*
 * Function to create a command link
 */
export function createCommandLink(command: string) {
    const escapedCommand = encodeURIComponent(command);
    const createCommandLink = `<span style="text-decoration: underline; cursor: pointer" data-command="${escapedCommand}" onclick="(function(){ let chatInput = document.getElementById('chat-input'); chatInput.value = decodeURIComponent('${escapedCommand}'); chatInput.focus(); return false; })();return false;">${command}</span>`;
    return createCommandLink;
}

/*
 * Function to format chat text content to remove any html tags from it.
 * Function to format user chat text content to remove any html tags from it.
 */
export function formatChatTextContent(messageContent: string) {
    const contentAsString = messageContent
        ?.trim()
        ?.replace(/^sk:\/\/.*$/gm, (match: string) => createCommandLink(match))
        ?.replace(/^!sk:.*$/gm, (match: string) => createCommandLink(match));
    return contentAsString;
}

/*
 * Function to format bot chat text content to remove any html tags from it.
 */
export function formatBotChatTextContent(messageContent: string): string {
    const contentAsString = formatChatTextContent(messageContent);

    // if we need to run any specific bot formatting commands, we can do so here

    return contentAsString;
}
/*
 * Formats text containing `\n` or `\r` into paragraphs.
 */
export function formatParagraphTextContent(messageContent = '') {
    messageContent = messageContent?.replaceAll('\r\n', '\n\r');

    return (
        <Body1>
            {messageContent.split('\n').map((paragraph, idx) => (
                <p
                    key={`paragraph-${idx}`}
                    style={
                        paragraph?.includes('\r')
                            ? {
                                  display: 'flex',
                                  marginLeft: tokens.spacingHorizontalL,
                                  fontFamily: customFonts.Lato
                              }
                            : {
                                  overflowWrap: 'anywhere',
                                  fontFamily: customFonts.Lato
                              }
                    }
                >
                    {paragraph}
                </p>
            ))}
        </Body1>
    );
}


/*
 * Function to replace citation links with indices matching the citation list.
 */
export function replaceCitationLinksWithIndices(formattedMessageContent: string, message: ChatMessage) {
    const citations = message.citations;
    if (citations) {
        citations.forEach((citation, index) => {
            const citationLink = citation.link;
            formattedMessageContent = formattedMessageContent?.replaceAll(citationLink? citationLink : '', (index + 1).toString());
        });
    }

    return formattedMessageContent;
}

/**
 * Gets message of error
 */
export function getErrorDetails(error: unknown) {
    return error instanceof Error ? error.message : String(error);
}

export function formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}